import React, { RefObject } from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { Row, UseRowStateInstanceProps } from "react-table";

import { required } from "validators/validation";

import styles from "./table.module.scss";

type Props = Pick<UseRowStateInstanceProps<any>, "setRowState"> & {
	tableRowClassName?: string;
	isOddRowBackground?: boolean;
	isInfinityScrollEnabled?: boolean;

	row: Row;
	onRowClick?: (row: { [key: string]: any }) => void;
	handleRowSubmit?: ({
		values,
		row,
		setRowState,
	}: {
		values: { [key: string]: any };
		row: { [key: string]: any };
		setRowState: any;
	}) => any;
	customRowClassName?: (row: any) => string | false;
	scrollRef?: ((node?: Element | null) => void) | RefObject<HTMLDivElement>;
};

export const TableRow = ({
	row,
	handleRowSubmit,
	setRowState,
	onRowClick,
	customRowClassName,
	tableRowClassName,
	scrollRef,
	isInfinityScrollEnabled,
	isOddRowBackground = false,
}: Props) => {
	const formMethods = useForm({
		defaultValues: row.values,
	});

	const {
		handleSubmit,
		register,
		formState: { errors },
		control,
		setValue,
		getValues,
		watch,
	} = formMethods;

	const getField = cell => {
		// That should return different types of fields. E.g. datepicker | input... etc
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const cellProps = {
			name: cell.column.id,
			defaultValue: cell.value,
			validator: required,
			register,
			setValue,
			getValues,
			control,
			errors,
			watch,
			className: styles.fixInputMargin,
		};

		if (fp.get("column.Cell.name", cell) !== "defaultRenderer")
			return cell.render("Cell", {
				row,
				cell,
				setRowState,
				cellProps,
			});

		if (row.state.edit && cell.column.editable) return <div />;

		return <span>{cell.value}</span>;
	};

	const editableRowClassName = row.state.edit ? "editableRow" : "";
	return (
		<FormProvider {...formMethods}>
			<Form
				{...row.getRowProps({
					className: clsx(
						styles.row,
						editableRowClassName,
						customRowClassName?.(row),
						tableRowClassName,
						isOddRowBackground
							? styles.oddRowBackground
							: styles.evenRowBackground,
					),
				})}
				onSubmit={handleSubmit(values => {
					handleRowSubmit?.({
						values,
						row,
						setRowState,
					});
				})}
				onClick={() => !row?.state?.edit && onRowClick?.(row)}
			>
				{row.cells.map(cell => {
					const column = cell.column as any;
					const classNameCell = column?.classNameCell;
					const alignRight = column?.alignRight;
					const isSticky = column?.isSticky;
					const isLastStickyColumn = column?.isLastStickyColumn;
					const cellStyle = column?.cellStyle;

					const cellProps = cell.getCellProps({
						style: cellStyle,
						className: clsx(
							styles.cell,
							classNameCell,
							alignRight && styles.alignRight,
							isSticky && styles.isSticky,
							isLastStickyColumn && styles.isLastStickyColumn,
						),
					});

					return (
						<div {...cellProps}>
							<div className={styles.cellInner}>
								{getField(cell)}
							</div>
						</div>
					);
				})}
				{isInfinityScrollEnabled && <div ref={scrollRef} />}
				<input type="submit" hidden />
			</Form>
		</FormProvider>
	);
};
